import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Card Primitive - Base card component with 35px radius
 *
 * Foundation for all mission card variants. Uses the OwnYou design
 * system tokens for consistent styling across PWA and Tauri.
 *
 * @see docs/architecture/OwnYou_architecture_v13.md Section 4.4, 4.5
 */
import { forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { cn } from '../utils/cn';
const cardVariants = cva(
// Base styles — Sprint 14 design system
'rounded-xl bg-white overflow-hidden', {
    variants: {
        variant: {
            default: 'shadow-sm',
            elevated: 'shadow-md',
            outline: 'border border-neutral-200',
            ghost: 'bg-transparent',
        },
        size: {
            default: 'w-card', // 180px mobile
            desktop: 'w-card-desktop', // 260px desktop
            full: 'w-full',
        },
        padding: {
            none: 'p-0',
            sm: 'p-2',
            md: 'p-4',
            lg: 'p-6',
        },
    },
    defaultVariants: {
        variant: 'default',
        size: 'default',
        padding: 'none',
    },
});
/**
 * Card primitive - foundation for mission cards
 *
 * @example
 * ```tsx
 * <Card variant="default" size="default">
 *   <CardImage src="..." alt="..." />
 *   <CardContent>
 *     <CardTitle>Product Name</CardTitle>
 *     <CardDescription>Description here</CardDescription>
 *   </CardContent>
 * </Card>
 * ```
 */
export const Card = forwardRef(({ className, variant, size, padding, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'div';
    return (_jsx(Comp, { className: cn(cardVariants({ variant, size, padding, className })), ref: ref, ...props }));
});
Card.displayName = 'Card';
/**
 * Card Header component
 */
export const CardHeader = forwardRef(({ className, ...props }, ref) => (_jsx("div", { ref: ref, className: cn('flex flex-col space-y-1.5 p-3', className), ...props })));
CardHeader.displayName = 'CardHeader';
/**
 * Card Title component
 */
export const CardTitle = forwardRef(({ className, ...props }, ref) => (_jsx("h3", { ref: ref, className: cn('text-subhead text-neutral-900 leading-tight', className), ...props })));
CardTitle.displayName = 'CardTitle';
/**
 * Card Description component
 */
export const CardDescription = forwardRef(({ className, ...props }, ref) => (_jsx("p", { ref: ref, className: cn('text-caption text-neutral-500', className), ...props })));
CardDescription.displayName = 'CardDescription';
/**
 * Card Content component
 */
export const CardContent = forwardRef(({ className, ...props }, ref) => (_jsx("div", { ref: ref, className: cn('p-3 pt-0', className), ...props })));
CardContent.displayName = 'CardContent';
/**
 * Card Footer component
 */
export const CardFooter = forwardRef(({ className, ...props }, ref) => (_jsx("div", { ref: ref, className: cn('flex items-center p-3 pt-0', className), ...props })));
CardFooter.displayName = 'CardFooter';
export const CardImage = forwardRef(({ className, radius = 'default', alt, ...props }, ref) => {
    const radiusClass = {
        default: 'rounded-sm',
        large: 'rounded-lg',
        none: 'rounded-none',
    }[radius];
    return (_jsx("img", { ref: ref, className: cn('w-full object-cover bg-neutral-200', radiusClass, className), alt: alt, ...props }));
});
CardImage.displayName = 'CardImage';
export { cardVariants };
//# sourceMappingURL=Card.js.map